import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { fetchResultById } from './api/surveyAPI';
import { Frown, Meh, Smile, Camera, Beer, Clock, Calendar, CalendarDays, Share2, ArrowLeft } from 'lucide-react';

interface SurveyResult {
  severity: number;
  painLocations: string[];
  consumption: string[];
  duration: string;
  doctor?: string;
}

const ResultPage: React.FC = () => {
  const { resultId } = useParams<{ resultId: string }>();
  const [result, setResult] = useState<SurveyResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    async function loadResult() {
      try {
        if (resultId) {
          const data = await fetchResultById(resultId);
          setResult(data);
        }
      } catch (err) {
        console.error(err);
        setError("Could not load hangover data.");
      }
    }
    loadResult();
  }, [resultId]);

  const getEmoji = (level: number) => {
    if (level <= 3) return <Smile className="text-green-500" size={32} />;
    if (level <= 6) return <Meh className="text-yellow-500" size={32} />;
    return <Frown className="text-red-500" size={32} />;
  };
  
  const getDurationIcon = (duration: string) => {
    switch (duration) {
      case 'just-today':
        return <Clock size={24} className="text-indigo-500" />;
      case 'tomorrow-too':
        return <Calendar size={24} className="text-indigo-500" />;
      case 'days':
        return <CalendarDays size={24} className="text-indigo-500" />;
      default:
        return <Clock size={24} className="text-indigo-500" />;
    }
  };
  
  const getDurationText = (duration: string) => {
    switch (duration) {
      case 'just-today':
        return "Just Today";
      case 'tomorrow-too':
        return "Tomorrow Too";
      case 'days':
        return "Several Days";
      default:
        return duration;
    }
  };
  
  const getConsumptionIcons = (consumption: string[]) => {
    return consumption.map((item, index) => {
      switch (item) {
        case 'beer':
          return <Beer key={index} size={24} className="text-amber-500" />;
        case 'wine':
          return <Beer key={index} size={24} className="text-red-500" />;
        case 'hard':
          return <Beer key={index} size={24} className="text-blue-500" />;
        default:
          return <Beer key={index} size={24} className="text-gray-500" />;
      }
    });
  };
  
  const handleShareClick = () => {
    if (navigator.share) {
      navigator.share({
        title: 'My Hangover Report',
        text: 'Check out my hangover report!',
        url: window.location.href,
      }).catch((error) => console.log('Error sharing:', error));
    } else {
      alert('Share functionality not available on your device. Copy the URL manually.');
    }
  };

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-950">
      <div className="p-8 bg-white dark:bg-gray-900 rounded-xl shadow-lg max-w-xl w-full">
        <div className="p-6 text-center text-red-500 dark:text-red-400">
          <Frown size={48} className="mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-2">Uh oh!</h2>
          <p>{error}</p>
          <button 
            onClick={() => window.history.back()} 
            className="mt-6 px-4 py-2 bg-gray-200 dark:bg-gray-800 rounded-lg text-gray-700 dark:text-gray-300 flex items-center justify-center mx-auto"
          >
            <ArrowLeft size={16} className="mr-2" /> Go Back
          </button>
        </div>
      </div>
    </div>
  );
  
  if (!result) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-950">
      <div className="p-8 bg-white dark:bg-gray-900 rounded-xl shadow-lg max-w-xl w-full">
        <div className="p-6 text-center text-gray-700 dark:text-gray-300">
          <div className="w-12 h-12 border-4 border-indigo-600 dark:border-indigo-400 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-xl">Loading your hangover report...</p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-950 py-8 px-4">
      <div className="max-w-xl mx-auto bg-white dark:bg-gray-900 rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-8 text-white">
          <h1 className="text-3xl font-bold mb-2 text-center">Your Hangover Report</h1>
          <p className="text-center opacity-90">ID: {resultId?.substring(0, 8)}...</p>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Severity */}
          <div className="mb-8 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
            <h2 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">Severity</h2>
            <div className="flex items-center justify-center gap-4">
              {getEmoji(result.severity)}
              <div className="flex items-center">
                <div className="h-2 w-64 bg-gradient-to-r from-green-400 to-red-500 rounded-lg">
                  <div 
                    className="h-5 w-5 rounded-full bg-white border-2 border-indigo-600 shadow-md relative bottom-1.5"
                    style={{ marginLeft: `calc(${(result.severity / 10) * 100}% - 10px)` }}
                  ></div>
                </div>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">
                {result.severity}/10
              </div>
            </div>
          </div>

          {/* Pain Locations */}
          <div className="mb-8 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
            <h2 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">Pain Locations</h2>
            <div className="flex flex-wrap gap-2 justify-center">
              {result.painLocations.map((location, index) => (
                <div key={index} className="bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-300 px-3 py-1 rounded-full">
                  {location.replace('svg:', '').replace('-', ' ')}
                </div>
              ))}
            </div>
          </div>

          {/* Consumption */}
          <div className="mb-8 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
            <h2 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">Consumption</h2>
            <div className="flex items-center justify-center gap-4 mb-3">
              {getConsumptionIcons(result.consumption)}
            </div>
            <div className="text-center text-gray-700 dark:text-gray-300">
              {result.consumption.join(', ')}
            </div>
          </div>

          {/* Duration */}
          <div className="mb-8 bg-gray-50 dark:bg-gray-800 p-4 rounded-lg">
            <h2 className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">Expected Duration</h2>
            <div className="flex items-center justify-center gap-3">
              {getDurationIcon(result.duration)}
              <span className="text-lg text-gray-900 dark:text-white">
                {getDurationText(result.duration)}
              </span>
            </div>
          </div>

          {/* Doctor's Note */}
          <div className="mb-8 border border-gray-200 dark:border-gray-700 p-4 rounded-lg">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Doctor's Note</h2>
            <p className="text-gray-700 dark:text-gray-300 italic">
              {result.severity <= 3 
                ? "Stop being a little pussy and order some Formosa."
                : result.severity <= 6
                ? "Goddamn that makes me so damn happy. You don't know how much your pain feeds me. THANK YOU!!!"
                : "This is the BEST NEWS I've heard all day. Your suffering is absolutely DELICIOUS. Please, PLEASE come back when it happens again!"}
            </p>
            <div className="mt-4 text-right text-sm text-gray-500 dark:text-gray-400">
              - Dr. {result.doctor || 'Matt'}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-4 mt-6">
            <button 
              onClick={handleShareClick}
              className="flex-1 flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-4 rounded-lg"
            >
              <Share2 size={18} /> Share Report
            </button>
            <button 
              onClick={() => window.history.back()}
              className="flex-1 flex items-center justify-center gap-2 border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-300 py-2 px-4 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              <ArrowLeft size={18} /> Back
            </button>
          </div>
        </div>
      </div>
      
      <div className="max-w-xl mx-auto mt-4 text-center text-sm text-gray-500 dark:text-gray-400">
        <p>Hangover data collected by Dr. {result.doctor || 'Matt'}'s Clinic.</p>
        <p>Remember to drink responsibly.</p>
      </div>
    </div>
  );
};

export default ResultPage;